import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "consent-content" }
const _hoisted_2 = {
  key: 0,
  class: "loading-container",
  role: "alert",
  "aria-busy": "true",
  "aria-live": "assertive"
}
const _hoisted_3 = { class: "loading-content" }

import { computed, ref, onMounted, onUnmounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { UserTracking } from "@/utils/UserTracking";

const privacy_terms_url =
  "https://dp-one-terms-conditions.s3.eu-central-1.amazonaws.com/BR+Master+T%26Cs+-+Doctor+Copilot+2024+.docx.pdf";

// =============================================================================
// Methods
// =============================================================================


export default /*@__PURE__*/_defineComponent({
  __name: 'AIAssistantStart',
  props: {
  isOpen: {
    type: Boolean,
    default: false
  },
  loadingStart: {
    type: Boolean,
    default: false
  },
  requireFirstConsent: {
    type: Boolean,
    default: true
  }
},
  emits: ["start", "close"],
  setup(__props, { emit: __emit }) {

/**
 * AIAssistantStart.vue - Consent Modal
 * 
 * Standalone modal for user consent before starting the AI Assistant.
 * Shows privacy policy information and requires user acceptance.
 */
const debugLog = (location: string, message: string, data?: Record<string, unknown>) => {
  console.log(`[ConsentModal:${location}] ${message}`, data || "");
};

// =============================================================================
// Props & Emits
// =============================================================================

const props = __props;

const emit = __emit;

// =============================================================================
// State
// =============================================================================

const { locale, t } = useI18n();
const isLoading = ref(true);
const hasConsented = ref(false);
const loadingMessage = ref('Iniciando assistente...');
let messageInterval: ReturnType<typeof setInterval> | null = null;

// =============================================================================
// Computed
// =============================================================================

const imgUrl = computed(() => {
  return `${process.env.BASE_URL}assets/img/ai-assistant-start-${locale.value}.png?v=2`;
});

const rotateLoadingMessage = () => {
  const messages = [
    'Iniciando assistente...',
    'Preparando modelo...',
    'Carregando informações...'
  ];
  let currentIndex = 0;

  messageInterval = setInterval(() => {
    currentIndex = (currentIndex + 1) % messages.length;
    loadingMessage.value = messages[currentIndex];
  }, 2000);
};

const handleClose = () => {
  debugLog("handleClose", `Close [isOpen=${props.isOpen}]`);
  emit("close");
};

const handleAccept = () => {
  debugLog("handleAccept", `Accept [hasConsented=${hasConsented.value}]`);
  
  if (!hasConsented.value) {
    localStorage.setItem('aiAssistant.hasConsented', 'true');
    hasConsented.value = true;
  }
  
  UserTracking.trackEvent("cta_clicked", {
    action: "initiate noa notes record",
    label: "accept terms and conditions",
    location: "medical episode",
    page_category: "patients",
    page_section: "patient profile",
    page_type: "",
  });
  
  emit("start");
  debugLog("handleAccept", "Start event emitted");
};

// =============================================================================
// Lifecycle
// =============================================================================

onMounted(() => {
  const storedConsent = localStorage.getItem('aiAssistant.hasConsented');
  hasConsented.value = storedConsent === 'true';
  isLoading.value = false;
  rotateLoadingMessage();
  debugLog("onMounted", `Mounted [hasConsented=${hasConsented.value}]`);
});

onUnmounted(() => {
  if (messageInterval) {
    clearInterval(messageInterval);
  }
});

// Watch for modal open state changes
watch(() => props.isOpen, (isOpen) => {
  debugLog("watch:isOpen", `Changed [${isOpen}]`);
});

return (_ctx: any,_cache: any) => {
  const _component_w_spinner = _resolveComponent("w-spinner")!
  const _component_w_text = _resolveComponent("w-text")!
  const _component_w_link = _resolveComponent("w-link")!
  const _component_w_button = _resolveComponent("w-button")!
  const _component_w_stack = _resolveComponent("w-stack")!
  const _component_w_spacer = _resolveComponent("w-spacer")!
  const _component_w_modal = _resolveComponent("w-modal")!

  return (_openBlock(), _createBlock(_component_w_modal, {
    "model-value": __props.isOpen,
    "onUpdate:modelValue": _cache[0] || (_cache[0] = (val) => { if (!val) handleClose() }),
    size: "medium",
    title: "Termos de Uso",
    "close-on-click-outside": false,
    onClose: handleClose
  }, {
    body: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        (__props.loadingStart)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _createVNode(_component_w_spinner, {
                  size: "small",
                  "aria-label": _unref(t)('startSection.loading')
                }, null, 8, ["aria-label"]),
                _createVNode(_component_w_text, {
                  class: "loading-message",
                  type: "body-bold"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(loadingMessage.value), 1)
                  ]),
                  _: 1
                })
              ])
            ]))
          : (_openBlock(), _createBlock(_component_w_spacer, {
              key: 1,
              all: "large"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_w_stack, {
                  direction: "column",
                  gap: "medium",
                  "justify-content": "center"
                }, {
                  default: _withCtx(() => [
                    (!hasConsented.value || __props.requireFirstConsent)
                      ? (_openBlock(), _createBlock(_component_w_text, { key: 0 }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_ctx.$t("startSection.privacyPolicyTextStart")) + " ", 1),
                            _createVNode(_component_w_link, {
                              target: "_blank",
                              href: privacy_terms_url
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(_ctx.$t("startSection.privacyPolicyLinkText")), 1)
                              ]),
                              _: 1
                            }),
                            _createTextVNode(" " + _toDisplayString(_ctx.$t("startSection.privacyPolicyTextEnd")) + " ", 1),
                            _cache[1] || (_cache[1] = _createElementVNode("br", null, null, -1)),
                            _createTextVNode(" " + _toDisplayString(_ctx.$t("startSection.privacyPolicyPatientConsent")), 1)
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true),
                    _createVNode(_component_w_stack, {
                      direction: "row",
                      gap: "small",
                      "justify-content": "flex-end"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_w_button, {
                          label: "Cancelar",
                          variant: "secondary",
                          onClick: handleClose
                        }),
                        _createVNode(_component_w_button, {
                          label: _ctx.$t('startSection.startButtonLabel'),
                          variant: "primary",
                          "trailing-icon": "arrow-right",
                          loading: __props.loadingStart,
                          onClick: handleAccept
                        }, null, 8, ["label", "loading"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }))
      ])
    ]),
    _: 1
  }, 8, ["model-value"]))
}
}

})